// Utility functions for RoutesPage
import { Customer } from '../../../api/mobiApi/domain/domain.types';
import { getVehicleColorById } from '../../../shared/constants/colors';


export const getVehicleColor = (vehicleId: number | string, vehicleType?: string): string => {
  // Use central color palette for consistent colors across the app
  return getVehicleColorById(vehicleId);
};

export const getVehicleDisplayName = (
  vehicleId: number | string,
  selectedVehicles: any[],
  fallbackIndex?: number
): string => {
  // Convert string vehicle_id like "vehicle_1" to number
  const numericId = typeof vehicleId === 'string'
    ? parseInt(vehicleId.replace('vehicle_', ''))
    : vehicleId;

  let vehicle = selectedVehicles.find(
    (v: any) => {
      const vId = v.id ?? v.vehicle_id ?? v.vehicleId;
      const vNumericId = typeof vId === 'string' ? parseInt(vId.replace('vehicle_', '')) : vId;
      return vNumericId === numericId;
    }
  );

  if (!vehicle && typeof fallbackIndex === 'number' && fallbackIndex >= 0 && fallbackIndex < selectedVehicles.length) {
    vehicle = selectedVehicles[fallbackIndex];
  }

  return vehicle?.name || vehicle?.plate || vehicle?.code || `Araç ${numericId}`;
};

/**
 * Merges duplicate customers that have the same name and location.
 * Combines their demands into a single customer entry.
 *
 * @param customers - Array of customers to deduplicate
 * @param locationTolerance - Coordinate difference tolerance in degrees (default: 0.0001 ~= 10 meters)
 * @returns Array of deduplicated customers with combined demands
 */
export const mergeDuplicateCustomers = (
  customers: Customer[],
  locationTolerance: number = 0.0001
): Customer[] => {
  if (!customers || customers.length === 0) {
    return [];
  }

  const mergedCustomers: Customer[] = [];
  const processedIndices = new Set<number>();

  for (let i = 0; i < customers.length; i++) {
    // Skip if already processed
    if (processedIndices.has(i)) {
      continue;
    }

    const currentCustomer = customers[i];
    let combinedDemand = currentCustomer.demand;
    const duplicateIndices: number[] = [];

    // Find all duplicates
    for (let j = i + 1; j < customers.length; j++) {
      if (processedIndices.has(j)) {
        continue;
      }

      const otherCustomer = customers[j];

      // Check if name matches (case-insensitive)
      const namesMatch = currentCustomer.name.trim().toLowerCase() ===
                         otherCustomer.name.trim().toLowerCase();

      // Check if locations are within tolerance
      const latDiff = Math.abs(currentCustomer.lat - otherCustomer.lat);
      const lonDiff = Math.abs(currentCustomer.lon - otherCustomer.lon);
      const locationsMatch = latDiff <= locationTolerance && lonDiff <= locationTolerance;

      if (namesMatch && locationsMatch) {
        combinedDemand += otherCustomer.demand;
        duplicateIndices.push(j);
        processedIndices.add(j);
      }
    }

    // Mark current customer as processed
    processedIndices.add(i);

    // Create merged customer entry
    mergedCustomers.push({
      ...currentCustomer,
      demand: combinedDemand,
      // Optionally add metadata about merging
      id: duplicateIndices.length > 0
        ? `${currentCustomer.id}_merged_${duplicateIndices.length + 1}`
        : currentCustomer.id,
    });
  }

  return mergedCustomers;
};